import { useState } from 'react';
import { Lock, Shield, Key, Globe, Settings } from 'lucide-react';

const authMethods = [
  { id: 'jwt', name: 'JWT Local Authentication', icon: Key, status: 'active', desc: 'Email/password login with JWT tokens', config: { accessExpiry: '30 minutes', refreshExpiry: '7 days', algorithm: 'HS256', maxAttempts: 5, lockoutDuration: '30 minutes' }},
  { id: 'google', name: 'Google OAuth 2.0', icon: Globe, status: 'active', desc: 'Sign in with Google workplace accounts', config: { clientId: 'CONFIGURED', scope: 'email, profile', allowedDomains: 'trivont.com', autoProvision: true }},
  { id: 'azure', name: 'Microsoft EntraID / SAML', icon: Shield, status: 'configured', desc: 'Enterprise SSO with Azure Active Directory', config: { tenantId: 'CONFIGURED', protocol: 'OAuth 2.0 + OIDC', samlEndpoint: 'Configured', autoProvision: true }},
];

const securitySettings = [
  { label: 'Minimum Password Length', value: '8 characters', type: 'setting' },
  { label: 'Require Special Characters', value: 'Enabled', type: 'toggle', on: true },
  { label: 'Require Uppercase', value: 'Enabled', type: 'toggle', on: true },
  { label: 'Password Expiry', value: '90 days', type: 'setting' },
  { label: 'Session Timeout', value: '30 minutes', type: 'setting' },
  { label: 'Two-Factor Authentication', value: 'Optional', type: 'setting' },
  { label: 'Audit Login Events', value: 'Enabled', type: 'toggle', on: true },
];

const statusColors = { active: 'bg-emerald-100 text-emerald-800', configured: 'bg-blue-100 text-blue-800', inactive: 'bg-slate-100 text-slate-600' };

export function AuthenticationPage() {
  return (
    <div data-testid="authentication-page">
      <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6 flex items-center gap-2">
        <Lock className="w-6 h-6 text-sky-500" /> Authentication & SSO Configuration
      </h2>

      <div className="bg-sky-50 dark:bg-sky-900/20 p-4 rounded-xl border border-sky-200 dark:border-sky-800 mb-6 flex items-start gap-3">
        <Shield className="w-5 h-5 text-sky-700 dark:text-sky-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-sky-900 dark:text-sky-300"><span className="font-semibold">Multi-Auth Support:</span> Users can authenticate via local JWT, Google OAuth, or Microsoft EntraID.</p>
      </div>

      <div className="grid gap-4 mb-8">
        {authMethods.map(method => {
          const Icon = method.icon;
          return (
            <div key={method.id} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900 rounded-xl flex items-center justify-center">
                    <Icon className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800 dark:text-white">{method.name}</h3>
                      <span className={`${statusColors[method.status]} px-2 py-0.5 rounded-full text-xs font-medium`}>{method.status}</span>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{method.desc}</p>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {Object.entries(method.config).map(([k, v]) => (
                        <div key={k} className="text-xs"><span className="text-slate-500 dark:text-slate-400">{k.replace(/([A-Z])/g, ' $1').trim()}: </span><span className="font-medium text-slate-700 dark:text-slate-300">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
                <button className="text-sm bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-3 py-1.5 rounded-lg text-slate-600 dark:text-slate-300 flex items-center gap-1.5"><Settings className="w-3.5 h-3.5" /> Configure</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm">
        <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-b dark:border-slate-700">
          <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2"><Shield className="w-4 h-4 text-slate-600 dark:text-slate-400" /> Security Settings</h3>
        </div>
        <div className="divide-y dark:divide-slate-700">
          {securitySettings.map((s) => (
            <div key={s.label} className="px-6 py-4 flex items-center justify-between">
              <span className="text-sm text-slate-700 dark:text-slate-300">{s.label}</span>
              {s.type === 'toggle' ? (
                <div className={`w-10 h-6 rounded-full relative cursor-pointer transition-colors ${s.on ? 'bg-sky-600' : 'bg-slate-300 dark:bg-slate-600'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${s.on ? 'left-5' : 'left-1'}`} />
                </div>
              ) : (
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900 px-3 py-1 rounded-lg">{s.value}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
